import { HASH_AUTO_CLOUD_RECORDING, HASH_MULTI_DEVICE_MODE_MULTI, HASH_MULTI_DEVICE_MODE_SINGLE, HASH_PREJOIN_DISABLED, HASH_REPLACE_PARTICIPANT, HASH_START_SCREEN_SHARING, HASH_START_SILENT, HASH_START_WITH_AUDIO_MUTED, HASH_START_WITH_VIDEO_MUTED, JWT_QUERY_PARAM, STORAGE_ACCESS_TOKEN, hashStartAudioMuted } from './constants';
import { saveActiveMeetingId } from './authStorage';
import logger from './logger';
/** 进会 URL 带 from=miniprogram 时写入，挂断后 URL 变化仍可识别。 */
const WX_MP_SESSION_KEY = 'jitsi.wechat.miniprogram';
/**
 * 小程序 web-view：从 URL 注入门户 token / meetingId，供结束会议写 ENDED。
 */
function bootstrapPortalSessionFromUrl() {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        const params = new URLSearchParams(window.location.search);
        const fromMp = params.get('from') === 'miniprogram';
        const meetingIdRaw = params.get('meetingId') || params.get('portalMeetingId');
        const portalToken = params.get('portalToken');
        const meetingId = meetingIdRaw ? Number(meetingIdRaw) : NaN;
        if (!fromMp && !meetingIdRaw && !portalToken) {
            return;
        }
        if (Number.isFinite(meetingId) && meetingId > 0) {
            saveActiveMeetingId(meetingId);
        }
        if (portalToken) {
            try {
                localStorage.setItem(STORAGE_ACCESS_TOKEN, portalToken);
                logger.info(`[${new Date().toISOString()}] 进会已从 URL 注入门户会话 | meetingId=${meetingIdRaw || ''}`
                    + ` fromMp=${fromMp}`);
            }
            catch (e) {
                logger.warn(`注入 accessToken 失败 | ${e?.message || e}`);
            }
        }
        // 去掉敏感 query，避免后续导航/日志继续带 portalToken
        if (portalToken || meetingIdRaw) {
            try {
                const clean = new URL(window.location.href);
                clean.searchParams.delete('portalToken');
                // meetingId 可保留给调试；也删以免泄露业务主键到分享链接
                clean.searchParams.delete('meetingId');
                clean.searchParams.delete('portalMeetingId');
                window.history.replaceState(null, '', clean.toString());
            }
            catch {
                // ignore
            }
        }
    }
    catch {
        // ignore
    }
}
bootstrapPortalSessionFromUrl();
/**
 * 若当前 URL 带小程序标记，则写入 sessionStorage。
 */
function persistWeChatMiniProgramFlagFromUrl() {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('from') === 'miniprogram') {
            sessionStorage.setItem(WX_MP_SESSION_KEY, '1');
        }
    }
    catch {
        // ignore
    }
}
// 首屏进会即打标，避免仅依赖瞬时 wx / URL。
persistWeChatMiniProgramFlagFromUrl();
/**
 * 是否在微信小程序 web-view 内。
 */
export function isWeChatMiniProgramWebView() {
    persistWeChatMiniProgramFlagFromUrl();
    try {
        if (sessionStorage.getItem(WX_MP_SESSION_KEY) === '1') {
            return true;
        }
    }
    catch {
        // ignore
    }
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('from') === 'miniprogram') {
            return true;
        }
    }
    catch {
        // ignore
    }
    try {
        // 微信注入的 JSSDK（web-view 内可用）
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const miniProgram = window?.wx?.miniProgram;
        if (miniProgram && typeof miniProgram.navigateBack === 'function') {
            return true;
        }
    }
    catch {
        // ignore
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram') {
        return true;
    }
    return false;
}
/**
 * 尝试通知小程序返回原生页。
 *
 * @param {boolean} endForAll - 是否主持人结束全员会议
 * @returns {boolean} 是否已发出返回指令
 */
function tryWeChatMiniProgramNavigateBack(endForAll = false) {
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const miniProgram = window?.wx?.miniProgram;
        if (!miniProgram) {
            return false;
        }
        try {
            if (typeof miniProgram.postMessage === 'function') {
                miniProgram.postMessage({
                    data: {
                        type: 'LEAVE_MEETING',
                        endForAll: Boolean(endForAll),
                        ts: Date.now()
                    }
                });
            }
        }
        catch {
            // ignore
        }
        // 优先 reLaunch：开发者工具里 navigateBack 常无效，且会停在 BlankPage 白屏
        if (typeof miniProgram.reLaunch === 'function') {
            logger.info('微信小程序 web-view：挂断后 reLaunch 回原生首页');
            miniProgram.reLaunch({ url: '/pages/home/home' });
            return true;
        }
        if (typeof miniProgram.redirectTo === 'function') {
            logger.info('微信小程序 web-view：挂断后 redirectTo 回原生首页');
            miniProgram.redirectTo({ url: '/pages/home/home' });
            return true;
        }
        if (typeof miniProgram.navigateBack === 'function') {
            logger.info('微信小程序 web-view：挂断后 navigateBack 回原生首页');
            miniProgram.navigateBack({ delta: 1 });
            return true;
        }
    }
    catch (e) {
        logger.warn(`微信小程序返回失败 | ${e?.message || e}`);
    }
    return false;
}
/**
 * 小程序 web-view 内挂断后回到原生页，避免落到 H5 门户登录页。
 *
 * 调用小程序返回 API 后，再跳转到专用退出页持续重试，避免停在 BlankPage 白屏。
 *
 * @param {Object} [options] - 可选参数
 * @param {boolean} [options.endForAll] - 主持人结束全员
 * @returns {boolean} 是否应按小程序路径处理（拦截 H5 门户）
 */
export function leaveToWeChatMiniProgram(options) {
    if (!isWeChatMiniProgramWebView()) {
        return false;
    }
    const endForAll = Boolean(options?.endForAll);
    tryWeChatMiniProgramNavigateBack(endForAll);
    const retry = () => {
        tryWeChatMiniProgramNavigateBack(endForAll);
    };
    try {
        document.addEventListener('WeixinJSBridgeReady', retry, { once: true });
    }
    catch {
        // ignore
    }
    setTimeout(retry, 50);
    setTimeout(retry, 300);
    // 关键：离开 React 白屏页，进入持续尝试返回的静态页
    try {
        const exitUrl = `${window.location.origin}/static/miniprogram-exit.html`
            + (endForAll ? '?endForAll=1' : '');
        logger.info(`微信小程序 web-view：跳转退出页 | ${exitUrl}`);
        window.location.replace(exitUrl);
    }
    catch (e) {
        logger.warn(`跳转小程序退出页失败 | ${e?.message || e}`);
    }
    return true;
}
/**
 * 生成数字会议号。
 */
export function generateMeetingNo(length) {
    const digits = [];
    for (let i = 0; i < length; i++) {
        digits.push(String(Math.floor(Math.random() * 10)));
    }
    return digits.join('');
}
/**
 * 构造带 JWT 的会议室 URL 并整页跳转（触发 jitsi-meet 加载 config + 入会）。
 *
 * 安全提示：当前将 jwt 放入 query 参数（?jwt=xxx），JWT 会出现在浏览器历史、
 * 服务器访问日志中，存在泄露风险。jitsi-meet 原生 parseJWTFromURLParams 优先
 * 从 URL hash 读取（#jwt=xxx），如需收紧可改为写入 url.hash 并统一两端行为
 * （涉及 HomePage/Electron 等多处调用，改动前需评估影响面）。
 */
export function navigateToMeeting(session, options) {
    const room = session.meetingNo || session.roomName;
    const url = new URL(window.location.origin);
    url.pathname = `/${encodeURIComponent(room)}`;
    url.searchParams.set(JWT_QUERY_PARAM, session.jitsiJwt);
    if (options?.displayName) {
        url.searchParams.set('userInfo.displayName', options.displayName);
    }
    // 与客户端一致：跳过预加入页，直接进会；可与屏幕共享 hash 叠加
    const hashParts = [HASH_PREJOIN_DISABLED];
    if (session.startWithScreenSharing) {
        hashParts.push(HASH_START_SCREEN_SHARING);
    }
    if (session.replaceParticipant
        || session.multiDeviceMode === 'SINGLE') {
        hashParts.push(HASH_MULTI_DEVICE_MODE_SINGLE);
        hashParts.push(HASH_REPLACE_PARTICIPANT);
    }
    else {
        hashParts.push(HASH_MULTI_DEVICE_MODE_MULTI);
    }
    if (typeof session.startAudioMuted === 'number' && session.startAudioMuted >= 0) {
        hashParts.push(hashStartAudioMuted(session.startAudioMuted));
    }
    if (session.autoCloudRecording) {
        hashParts.push(HASH_AUTO_CLOUD_RECORDING);
    }
    if (options?.startWithAudioMuted) {
        hashParts.push(HASH_START_WITH_AUDIO_MUTED);
    }
    if (options?.startWithVideoMuted) {
        hashParts.push(HASH_START_WITH_VIDEO_MUTED);
    }
    if (options?.startSilent) {
        hashParts.push(HASH_START_SILENT);
    }
    url.hash = hashParts.join('&');
    // 持久化门户会议 ID，供会中结束全体会议写 DB ENDED
    if (session.id) {
        saveActiveMeetingId(session.id);
    }
    logger.info(`跳转会议室 | room=${room} meetingId=${session.id} prejoinDisabled=true`
        + ` screenShare=${Boolean(session.startWithScreenSharing)}`
        + ` multiDeviceMode=${session.multiDeviceMode || 'MULTI'}`
        + ` replaceParticipant=${Boolean(session.replaceParticipant)}`
        + ` startAudioMuted=${session.startAudioMuted ?? '-'}`
        + ` autoCloudRecording=${Boolean(session.autoCloudRecording)}`
        + ` startWithAudioMuted=${Boolean(options?.startWithAudioMuted)}`
        + ` startWithVideoMuted=${Boolean(options?.startWithVideoMuted)}`
        + ` startSilent=${Boolean(options?.startSilent)}`);
    window.location.href = url.toString();
}
/**
 * 解析 tokenAuthUrl 落地时的查询参数。
 */
export function parsePortalAuthQuery(search) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const portalAuth = params.get('portalAuth');
    const room = params.get('room') || '';
    const state = params.get('state') || '';
    return {
        isPortalAuth: portalAuth === '1',
        room,
        state
    };
}
/**
 * 手机号脱敏：13812345678 → 138****5678。
 */
export function maskPhone(phone) {
    if (!phone || phone.length < 7) {
        return '';
    }
    return `${phone.slice(0, 3)}****${phone.slice(-4)}`;
}
/**
 * tokenAuthUrl 的 state 是否表示 JWT 换票（已在会中重签，不占新设备）。
 *
 * @param {string} state - URL state 参数
 * @returns {boolean}
 */
export function isPortalAuthJwtRefresh(state) {
    if (!state) {
        return false;
    }
    const candidates = [state];
    try {
        candidates.push(decodeURIComponent(state));
    }
    catch {
        // ignore
    }
    try {
        candidates.push(decodeURIComponent(decodeURIComponent(state)));
    }
    catch {
        // ignore
    }
    for (const raw of candidates) {
        const objects = [];
        try {
            objects.push(JSON.parse(raw));
        }
        catch {
            // ignore
        }
        try {
            objects.push(JSON.parse(atob(raw)));
        }
        catch {
            // ignore
        }
        for (const obj of objects) {
            if (obj && (obj.refreshToken || obj.refresh === true)) {
                return true;
            }
        }
    }
    return false;
}
